import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from "react";
import IArborUserTypeMapping from "../../../../models/misconfig/arbor/userTypes/ArborUserTypeMapping";
import IFormWithConfig from "../../../../models/misconfig/FormWithConfig";
import { StandardMappingTemplates } from "../../../../app/enums";
import { Col, Row } from "react-bootstrap";
import React from "react";
import IConfigJson from "../../../../models/misconfig/ConfigJson";
import ArborStaffUserTypeEdit from "./arbor-staff-usertype-edit";
import ArborStudentUserTypeEdit from "./arbor-student-usertype-edit";
import ArborApplicantUserTypeEdit from "./arbor-applicant-usertype-edit";

export interface IArborUserTypeEditProps extends IFormWithConfig {
    selectedUserType: StandardMappingTemplates;
    isNew: boolean;
}

export interface IArborUserTypeSubEditProps extends IConfigJson {
    handleChange: (key: string, value: any) => void;
    firstCol: number;
    secondCol: number;
}

const ArborUserTypeEdit = forwardRef((props: IArborUserTypeEditProps, ref) => {
    const [arborConfig, setArborConfig] = useState<IArborUserTypeMapping | null>(null);    
    const [userTypePrefix, setUserTypePrefix] = useState<string>('');
    const [userTypePrefixEdited, setUserTypePrefixEdited] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const subProviderConfigRef = useRef<any>(null);
    const hasInitializedRef = useRef<boolean>(false);

    const handleChangeUserTypePrefix = (key: string, value: any) => {
        if(arborConfig === null) {
            return;
        }        
        setUserTypePrefixEdited(true);
        const updatedConfig = {...arborConfig, [key]: value };
        setArborConfig(updatedConfig);
        setUserTypePrefix(value);
        props.setJsonString(JSON.stringify(updatedConfig));
    }    

    const handleChangeExternal = useCallback((key: string, value: any) => {        
        setArborConfig(prev => {
                let current: IArborUserTypeMapping | null = prev;
                if(current === null) {
                    current = { UserTypePrefix: '' };
                }
                const updated = { ...current, [key]: value };         
                props.setJsonString(JSON.stringify(updated));
                return updated;
            });
    }, [props]);

    useEffect(() => {  
        if(!props.configJson) {
            hasInitializedRef.current = false;
            setArborConfig({
                UserTypePrefix: '',
            });
            setUserTypePrefix('');
            setUserTypePrefixEdited(false);
        } else if(props.configJson && !hasInitializedRef.current) {
            const configObj: IArborUserTypeMapping = JSON.parse(props.configJson);            
            setArborConfig(configObj);
            setUserTypePrefix(configObj?.UserTypePrefix || ''); 
            hasInitializedRef.current = true;                                 
        }                  
    }, [props.configJson, props.isNew]);    

    useEffect(() => {
        if(!props.isNew || userTypePrefixEdited) {
            return;
        }
        let prefix = 'P'; 
        if(Number(props.selectedUserType) === StandardMappingTemplates.Staff) {
            prefix = 'S';
        } else if(Number(props.selectedUserType) === StandardMappingTemplates.None) {
            prefix = '';
        }
        if(prefix === userTypePrefix) {
            return;
        }
        setUserTypePrefix(prefix);
        const updatedConfig = {...arborConfig!, UserTypePrefix: prefix };
        setArborConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    }, [props.isNew, userTypePrefixEdited, props.selectedUserType, arborConfig, props, userTypePrefix]);
    
    useImperativeHandle(ref, () => ({
        validate: function() : boolean {
            const newErrors: { [key: string]: string } = {};

            let isSubValid = true;
            if(subProviderConfigRef.current) {
                isSubValid = subProviderConfigRef.current.validate();
            }
            
            if(!arborConfig?.UserTypePrefix || arborConfig.UserTypePrefix.trim() === '') {
                newErrors['UserTypePrefix'] = 'User Type Prefix is required';
            }
            
            setErrors(newErrors);

            return Object.keys(newErrors).length === 0 && isSubValid;
        }
    }));        

    return (
        <React.Fragment>
             <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2">User type prefix</Col>
                <Col md={props.secondCol}>
                    <input className={`form-control mt-1 ms-0 w-100 ${(errors.UserTypePrefix) ? 'is-invalid' : ''}`} type="text" value={userTypePrefix} 
                        required maxLength={5} onChange={e => handleChangeUserTypePrefix('UserTypePrefix', e.target.value)} />      
                    {errors.UserTypePrefix && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>{errors.UserTypePrefix}</div>}                  
                </Col>
            </Row>    
            {   
                Number(props.selectedUserType) === StandardMappingTemplates.Staff ? 
                <ArborStaffUserTypeEdit
                    ref={subProviderConfigRef}
                    handleChange={handleChangeExternal}
                    firstCol={props.firstCol}
                    secondCol={props.secondCol} 
                    configJson={ props.configJson } />
                : Number(props.selectedUserType) === StandardMappingTemplates.Student ?
                <ArborStudentUserTypeEdit
                    ref={subProviderConfigRef}
                    handleChange={handleChangeExternal}
                    firstCol={props.firstCol}
                    secondCol={props.secondCol} 
                    configJson={ props.configJson } />
                : Number(props.selectedUserType) === StandardMappingTemplates.Applicants ?
                <ArborApplicantUserTypeEdit
                    ref={subProviderConfigRef}
                    handleChange={handleChangeExternal}
                    firstCol={props.firstCol}
                    secondCol={props.secondCol} 
                    configJson={ props.configJson } />
                : null
            }
        </React.Fragment>
    );        
});

export default ArborUserTypeEdit;
   
